import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";

function fmtErr(d) {
  if (d == null) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
}

export default function Auth() {
  const nav = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "Explorer" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
      nav("/list/dashboard");
    } catch (ex) { setErr(fmtErr(ex.response?.data?.detail) || ex.message); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-obsidian text-ivory">
      <Header variant="list" />
      <section className="pt-28 pb-16 px-6 max-w-md mx-auto">
        <p className="eyebrow mb-4">The LIST by LUXEWELL</p>
        <h1 className="font-display text-3xl tracking-tight mb-2">{mode === "login" ? "Welcome Back" : "Request Access"}</h1>
        <p className="font-headline italic text-ivory/60 mb-8">The right relationship can change the trajectory of a business.</p>
        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <input data-testid="auth-name" required placeholder="Full Name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-transparent border border-gold/30 px-4 py-3 text-sm outline-none focus:border-gold" />
          )}
          <input data-testid="auth-email" required type="email" placeholder="Email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full bg-transparent border border-gold/30 px-4 py-3 text-sm outline-none focus:border-gold" />
          <div className="relative">
            <input data-testid="auth-password" required type={show ? "text" : "password"} placeholder="Password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full bg-transparent border border-gold/30 px-4 py-3 pr-12 text-sm outline-none focus:border-gold" />
            <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Hide password" : "Show password"} data-testid="auth-password-toggle"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/60 hover:text-gold">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
          </div>
          {err && <p data-testid="auth-error" className="text-sm text-red-400">{err}</p>}
          <button data-testid="auth-submit" disabled={busy} className="btn-lux w-full text-center bg-gold text-obsidian hover:bg-ivory">
            {busy ? "…" : mode === "login" ? "Sign In" : "Create Account"}
          </button>
        </form>
        <button data-testid="auth-toggle" onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
          className="eyebrow !text-[0.6rem] mt-6 text-ivory/50 hover:text-gold">
          {mode === "login" ? "Need access? Request an account →" : "Already a member? Sign in →"}
        </button>
        <button type="button" onClick={() => nav("/list")} data-testid="auth-back" className="eyebrow !text-[0.6rem] mt-4 block text-ivory/40 hover:text-gold">← Back</button>
      </section>
    </div>
  );
}
